import {memo, useEffect, useState} from 'react';

import {useSelector} from 'react-redux';
import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {selectIsUserLoggedIn, selectUserLoaded} from '~/shared/store/storeModules/user/userSelectors';
import {LocalStorageKeys} from '~/shared/consts/localStorageConsts';
import {checkTimePastWithHours} from '~/shared/utils/checkTimePast';
import PieButton from '~/shared/theme/pie/PieButton';
import {flipOnLTR, media} from '~/shared/theme/utils';
import {body16Bold, body16Normal, body20Bold} from '~/shared/theme/typography';
import renderingForRobot from '~/shared/services/renderingForRobot';

const SIX_MONTHS_IN_DAYS = 180;

const Wrapper = styled.div`
  position: fixed;
  direction: ${({theme}) => theme.direction};
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${({theme}) => theme.zIndex.cookieConsent};
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({theme}) => theme.colors.layoutDark};
  color: ${({theme}) => theme.colors.primaryText};
  padding: 16px;
  align-items: start;
  border-radius: 12px 12px 0 0;
  ${media.minLargeMobile`
    padding: 24px;
    margin: 0 16px 16px;
    border-radius: 12px;
  `}
`;

const Title = styled.h2`
  ${body20Bold}
`;

const BodyText = styled.p`
  ${body16Normal}
  margin-top: 4px;

  a {
    color: ${({theme}) => theme.colors.primaryText};
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
`;

const ConfirmButton = styled(PieButton)`
  margin-top: 16px;
  width: 100%;
  height: 44px;
  ${body16Bold}
  ${media.minLargeMobile`
    width: fit-content;
    ${flipOnLTR`
      margin-right: auto;
    `}
  `}
`;

const CookieConsent = () => {
  const userLoaded = useSelector(selectUserLoaded);
  const isLoggedIn = useSelector(selectIsUserLoggedIn);
  const [showBanner, setShowBanner] = useState(false);
  const {t} = getLocalizationService();

  useEffect(() => {
    if (renderingForRobot) {
      return;
    }

    if (!userLoaded || isLoggedIn) {
      setShowBanner(false);
      return;
    }

    const stored = window?.localStorage?.getItem(LocalStorageKeys.COOKIE_CONSENT_TIMESTAMP);
    const is6MonthsPast = checkTimePastWithHours({storedDate: stored, days: SIX_MONTHS_IN_DAYS});

    setShowBanner(!stored || is6MonthsPast);
  }, [userLoaded, isLoggedIn]);

  const handleConfirm = () => {
    if (window?.localStorage) {
      window.localStorage.setItem(LocalStorageKeys.COOKIE_CONSENT_TIMESTAMP, String(new Date()));
    }
    setShowBanner(false);
  };

  if (renderingForRobot) {
    return null;
  }

  return (
    <Wrapper data-test-id="cookie-consent">
      {showBanner && (
        <Root data-test-id="cookie-consent-content">
          <Title data-test-id="cookie-consent-title">Cookies</Title>
          <BodyText
            data-test-id="cookie-consent-body"
            dangerouslySetInnerHTML={{__html: t('cookie_consent_body')}}
          />
          <ConfirmButton onClick={handleConfirm} variant="brandMedium" testId="cookie-consent-confirm">
            {t('i_understand')}
          </ConfirmButton>
        </Root>
      )}
    </Wrapper>
  );
};

export default memo(CookieConsent);
